import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Switch } from '../ui/switch';
import TripCountdownWidget from './TripCountdownWidget';
import {
  Plane,
  Users,
  BookOpen,
  Calendar,
  Heart,
  User,
  Sparkles,
  ArrowRight,
  DollarSign,
  CreditCard,
  MessageCircle,
  Shuffle
} from 'lucide-react';

export default function DashboardHomeTab({
  user,
  stats,
  bookings,
  trips,
  conversations,
  tripInterest,
  setTripInterest,
  handleSaveInterest,
  setActiveTab
}) {
  return (
    <div className="space-y-6">
      {/* Stats Tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                <Plane className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-primary">{stats.total_trips || bookings.length}</p>
                <p className="text-xs text-muted-foreground">Trips Booked</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                <DollarSign className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-primary">
                  ${(stats.total_paid || bookings.reduce((sum, b) => sum + (b.amount_paid || 0), 0)).toLocaleString()}
                </p>
                <p className="text-xs text-muted-foreground">Total Paid</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center">
                <CreditCard className="h-5 w-5 text-orange-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-primary">
                  ${(stats.balance_due || bookings.reduce((sum, b) => sum + (b.remaining_amount || 0), 0)).toLocaleString()}
                </p>
                <p className="text-xs text-muted-foreground">Balance Due</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                <Calendar className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-primary">
                  {stats.upcoming_trips || bookings.filter(b => b.status === 'confirmed' || b.status === 'pending_payment').length}
                </p>
                <p className="text-xs text-muted-foreground">Upcoming</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Messages Notification */}
      {(stats.unread_messages > 0 || conversations.length > 0) && (
        <Card className="border-secondary/50 bg-secondary/5">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-secondary/20 flex items-center justify-center">
                  <MessageCircle className="h-5 w-5 text-secondary" />
                </div>
                <div>
                  <p className="font-medium text-primary">
                    {stats.unread_messages > 0 
                      ? `You have ${stats.unread_messages} unread message${stats.unread_messages > 1 ? 's' : ''}`
                      : `${conversations.length} conversation${conversations.length > 1 ? 's' : ''}`
                    }
                  </p>
                  <p className="text-xs text-muted-foreground">Connect with fellow travelers</p>
                </div>
              </div>
              <Button size="sm" variant="outline" onClick={() => setActiveTab('matching')}>
                View Messages
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Trip Countdown Widget */}
      <TripCountdownWidget bookings={bookings} />

      {/* Quick Links to Main Pages */}
      <div className="grid sm:grid-cols-3 gap-4">
        <Link to="/group-adventures">
          <Card className="hover:shadow-lg transition-shadow cursor-pointer h-full bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200">
            <CardContent className="p-6 text-center">
              <Users className="h-10 w-10 text-orange-600 mx-auto mb-3" />
              <h3 className="font-heading font-semibold text-primary mb-1">Group Adventures</h3>
              <p className="text-xs text-muted-foreground">Join scheduled group tours with set departure dates</p>
            </CardContent>
          </Card>
        </Link>
        <Link to="/solo-adventures">
          <Card className="hover:shadow-lg transition-shadow cursor-pointer h-full bg-gradient-to-br from-cyan-50 to-cyan-100 border-cyan-200">
            <CardContent className="p-6 text-center">
              <User className="h-10 w-10 text-cyan-600 mx-auto mb-3" />
              <h3 className="font-heading font-semibold text-primary mb-1">Solo Adventures</h3>
              <p className="text-xs text-muted-foreground">Explore solo options or find a travel partner</p>
            </CardContent>
          </Card>
        </Link>
        <Link to="/customize">
          <Card className="hover:shadow-lg transition-shadow cursor-pointer h-full bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
            <CardContent className="p-6 text-center">
              <Shuffle className="h-10 w-10 text-purple-600 mx-auto mb-3" />
              <h3 className="font-heading font-semibold text-primary mb-1">Customize Your Trip</h3>
              <p className="text-xs text-muted-foreground">Create a custom itinerary for your group</p>
            </CardContent>
          </Card>
        </Link>
      </div>

      {/* Express Interest for Matching */}
      <Card id="trip-interest-section">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Heart className="h-5 w-5 text-secondary" />
            Looking for Travel Partners?
          </CardTitle>
          <CardDescription>
            Share which trips you're interested in to connect with other solo travelers before you book
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-sm font-medium mb-1 block">Which trip are you interested in?</Label>
              <Select value={tripInterest.tripId} onValueChange={(v) => setTripInterest({ ...tripInterest, tripId: v })}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a trip" />
                </SelectTrigger>
                <SelectContent>
                  {trips.map(trip => (
                    <SelectItem key={trip.id} value={trip.id}>{trip.title}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm font-medium mb-1 block">Preferred travel dates</Label>
              <Input
                placeholder="e.g., September 2025, Flexible"
                value={tripInterest.preferredDates}
                onChange={(e) => setTripInterest({ ...tripInterest, preferredDates: e.target.value })}
              />
            </div>
          </div>
          <div className="flex flex-wrap gap-6">
            <div className="flex items-center gap-2">
              <Switch
                checked={tripInterest.lookingForRoommate}
                onCheckedChange={(v) => setTripInterest({ ...tripInterest, lookingForRoommate: v })}
              />
              <Label className="text-sm">Looking for a roommate</Label>
            </div>
            <div className="flex items-center gap-2">
              <Switch
                checked={tripInterest.lookingForTravelPartner}
                onCheckedChange={(v) => setTripInterest({ ...tripInterest, lookingForTravelPartner: v })}
              />
              <Label className="text-sm">Looking for a travel partner</Label>
            </div>
          </div>
          <div>
            <Label className="text-sm font-medium mb-1 block">Quick bio (optional)</Label>
            <Textarea
              placeholder="Tell other travelers a bit about yourself..."
              value={tripInterest.bio}
              onChange={(e) => setTripInterest({ ...tripInterest, bio: e.target.value })}
              rows={2}
            />
          </div>
          <Button onClick={handleSaveInterest} className="bg-secondary hover:bg-secondary/90">
            <Heart className="h-4 w-4 mr-2" />
            Save My Interest
          </Button>
        </CardContent>
      </Card>

      {/* How It Works & Discover */}
      <div className="grid md:grid-cols-2 gap-6">
        <Link to="/how-it-works">
          <Card className="hover:shadow-lg transition-shadow h-full">
            <CardContent className="p-6">
              <BookOpen className="h-8 w-8 text-secondary mb-3" />
              <h3 className="font-heading font-semibold text-primary mb-2">How It Works</h3>
              <p className="text-sm text-muted-foreground">
                Learn about our booking process, payment plans, and what to expect on your adventure.
              </p>
              <span className="text-secondary text-sm font-medium inline-flex items-center mt-3">
                Learn more <ArrowRight className="h-4 w-4 ml-1" />
              </span>
            </CardContent>
          </Card>
        </Link>
        <Link to="/resources">
          <Card className="hover:shadow-lg transition-shadow h-full">
            <CardContent className="p-6">
              <Sparkles className="h-8 w-8 text-secondary mb-3" />
              <h3 className="font-heading font-semibold text-primary mb-2">Travel Hub</h3>
              <p className="text-sm text-muted-foreground">
                Explore travel tips, destination guides, packing lists, and inspiration for your next trip.
              </p>
              <span className="text-secondary text-sm font-medium inline-flex items-center mt-3">
                Explore articles <ArrowRight className="h-4 w-4 ml-1" />
              </span>
            </CardContent>
          </Card>
        </Link>
      </div>
    </div>
  );
}
